import {
	useLocation,
	useNavigate,
	useNavigation,
	useLoaderData,
} from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import PlentyBody from '#app/components/typography/plenty-body'
import { Icon } from '#app/components/ui/icon'
import { type ClerkFacet } from '#app/constants/clerk-api'
import useAmplitudeContext from '#app/hooks/use-amplitude-context'
import { type loader } from '#app/routes/_public+/products+/$slug'
import { subtractArrays } from '#app/utils/array-utils'
import { getLanguageFromShortHand } from '#app/utils/localisation/lokalisation-utilities'
import {
	getKeysByValue,
	mainColorsMapped,
	specialMapping,
} from '../filters/colors-utils'
import { type AppliedFilter } from '.'

interface AppliedFilterLabelProps {
	appliedFilter: AppliedFilter
}

const AppliedFilterLabel = ({ appliedFilter }: AppliedFilterLabelProps) => {
	const { facetsData, locale } = useLoaderData<typeof loader>()
	const { trackAmplitudeEvent } = useAmplitudeContext()
	const { t } = useTranslation()
	const navigation = useNavigation()
	const location = useLocation()
	const navigate = useNavigate()

	const isKids = location.pathname.includes('kids') ?? false
	const langKey = getLanguageFromShortHand(locale)

	const handleRemoveFilter = () => {
		const params = new URLSearchParams(
			navigation.state === 'idle'
				? location.search
				: navigation.location.search,
		)

		// Use a map to track parameters
		const paramMap = new Map<string, string[]>()

		// Populate the map with the existing parameters
		params.forEach((value, key) => {
			if (!paramMap.has(key)) {
				paramMap.set(key, [])
			}
			paramMap.get(key)!.push(value)
		})
		// Modify the values based on the applied filter
		if (appliedFilter.isColor) {
			const values = paramMap.get(appliedFilter.key) || []
			const specialTerms = getKeysByValue(
				specialMapping,
				appliedFilter.value,
				langKey,
			)
			const newValues = values.filter(
				value =>
					value !== appliedFilter.value &&
					!specialTerms.includes(value.toLowerCase()),
			)
			paramMap.set(appliedFilter.key, newValues)
		} else if (appliedFilter.isMaterials) {
			const values = paramMap.get(appliedFilter.key) || []
			const term =
				facetsData.facets?.materials?.find(
					(materials: ClerkFacet) => materials.value === appliedFilter.value,
				) || []

			const newValues = subtractArrays(values, [term.value])

			paramMap.set(appliedFilter.key, newValues)
		} else if (appliedFilter.isSize && isKids) {
			const values = paramMap.get(appliedFilter.key) || []
			const term =
				facetsData.facets?.sizes?.find(
					(size: ClerkFacet) => size.value === appliedFilter.value,
				)?.terms || []
			const newValues = subtractArrays(values, term)
			paramMap.set(appliedFilter.key, newValues)
		} else if (appliedFilter.key.includes('category')) {
			const splittedKey = appliedFilter.key.split('-')
			const englishValue = splittedKey[1]
			const key = splittedKey[0]
			const values = paramMap.get(key) || []

			const newValues = values.filter(value => value !== englishValue)
			paramMap.set(key, newValues)
		} else {
			const values = paramMap.get(appliedFilter.key) || []
			const newValues = values.filter(value => value !== appliedFilter.value)
			paramMap.set(appliedFilter.key, newValues)
		}

		// Handle special case for 'gender' key
		if (
			appliedFilter.key === 'gender' &&
			(paramMap.get('gender') || []).length === 1
		) {
			paramMap.delete('gender')
		}

		// Remove the pagination parameter
		paramMap.delete('p')

		// Reconstruct the URLSearchParams
		const newParams = new URLSearchParams()
		paramMap.forEach((values, key) => {
			values.forEach(value => {
				newParams.append(key, value)
			})
		})
		trackAmplitudeEvent('plp remove filter label')
		// Navigate to the updated URL
		navigate({
			pathname: '.',
			search: newParams.toString(),
		})
	}

	return (
		<div
			className="flex cursor-pointer flex-row items-center gap-2 rounded bg-beige-50 p-2"
			onClick={handleRemoveFilter}
			data-testid="applied-filter-label"
		>
			<div className="flex flex-row items-center gap-1">
				<PlentyBody>{appliedFilter.isSize && t('common:size')}</PlentyBody>
				{appliedFilter.isColor && <AppliedColor color={appliedFilter.value} />}
				<PlentyBody>
					{appliedFilter.isGender
						? appliedFilter.isGender
						: appliedFilter.value}
				</PlentyBody>
			</div>
			<Icon name="close"></Icon>
		</div>
	)
}

const AppliedColor = ({ color }: { color: string }) => {
	// look in mainColorsMapped for the color in english or danish
	const mainColor = mainColorsMapped.find(
		facet => facet.name.en === color || facet.name['da'] === color,
	)
	return (
		<div
			className="h-4 w-4 rounded-full border-2 border-white"
			style={{ background: `${mainColor?.rgb}` }}
		></div>
	)
}

export default AppliedFilterLabel
